import { BlogCard } from './types';

export const fluVaccination: BlogCard[] = [
  {
    title: {
      content:
        '### **Flu vaccination for pregnant women: Safety, Timing and What to Expect** ',
      styles: {
        h2: 'font-mulish font-bold text-royal-875 mb-0 text-base md:text-lg leading-tight',
        h3: 'font-mulish font-bold text-royal-875 text-base md:text-lg leading-tight',
        strong:
          'font-mulish font-bold text-royal-875 text-base md:text-lg leading-tight',
        p: 'mb-0 mt-3',
      },
      topicsStyles: {
        h2: 'font-mulish text-theme-primary text-3xl md:text-4xl mb-4 md:mb-6 font-black leading-tight line-clamp-2',
        h3: 'font-mulish text-theme-primary text-3xl md:text-4xl mb-4 md:mb-6 pt-2 font-black',
        strong: 'font-mulish text-text-subtitle font-bold',
        p: 'font-mulish mb-0 text-2xl md:text-3xl p-2',
      },
    },
    description: {
      content:
        'Flu vaccination for pregnant women is recommended in the UK at any stage of pregnancy, because pregnancy can make flu more serious and the jab helps protect both you and your baby.',
      styles: {
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
      },
    },
    metaTitle: 'Flu Vaccination for Pregnant Women: Is It Safe? | LIPS',
    imageUrl: 'flu-vaccination-blog-hero-image.webp',
    bannerUrl: 'flu-vaccination-blog-hero-image.webp',
    slug: 'flu-vaccination-for-pregnant-women',
    contentType: 'article',
    speciality: 'GP - General Practice',
    iconUrl: 'yu-zhen-lau.webp',
    doctorSlug: 'yu-zhen-lau',
    date: {
      content: '28, October 2026',
      iconUrl: '/images/blogs/calendar.svg',
      styles: {
        p: 'text-gray-500 text-xs',
      },
    },
    postimonial: {
      content: 'MBBS, MRCGP \n General Practitioner',
      styles: {
        p: 'text-xl whitespace-pre-line',
      },
    },
    author: {
      content: 'Dr Yu Zhen Lau',
      styles: {
        p: 'text-indigo-650 font-bold text-xs',
        h3: 'text-theme-primary font-black text-2xl md:text-4xl',
      },
    },
    bannerDescription: {
      content: `Flu vaccination for pregnant women is recommended in the UK at any stage of pregnancy, because pregnancy can make flu more serious and the jab helps protect both you and your baby. It is natural to think carefully about anything you put into your body while pregnant, so clear information helps. If you would like to talk it through first, a GP appointment at LIPS Healthcare, Battersea Power Station, gives you time to ask questions. This guide explains why it is recommended, how safe it is, how it fits with other pregnancy vaccines and what happens at the appointment.

> <span style="font-weight: bold; color: #2A347A;">At a glance</span>
>
> Recommended at any stage of pregnancy.
> It is an inactivated injection, so it cannot give you flu.
> Some protection passes to your baby for their first few months.`,
      styles: {
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
        strong: 'font-bold text-text-default',
        blockquote:
          'border-l-4 border-theme-primary bg-[#E0E7FF] rounded-lg pt-4 px-4 pb-2 my-8 text-sm text-text-default not-italic',
      },
    },
    tableOfContents: {
      heading: {
        content: '### TABLE OF CONTENTS:',
        styles: {
          h3: 'font-Mulish font-bold text-royal-875 mb-3 text-lg md:text-xl',
        },
      },
      content: [
        {
          text: 'Why is the flu jab recommended in pregnancy?',
          id: 'why-is-the-flu-jab-recommended-in-pregnancy',
        },
        {
          text: 'Is the flu jab safe at any stage of pregnancy?',
          id: 'is-the-flu-jab-safe-at-any-stage-of-pregnancy',
        },
        {
          text: 'Can you have the flu jab with other pregnancy vaccines?',
          id: 'can-you-have-the-flu-jab-with-other-pregnancy-vaccines',
        },
        {
          text: 'What happens at a flu jab appointment when pregnant?',
          id: 'what-happens-at-a-flu-jab-appointment-when-pregnant',
        },
        {
          text: 'Frequently asked questions about flu vaccination in pregnancy',
          id: 'frequently-asked-questions',
        },
      ],
      styles: {
        p: 'text-lg text-teal-500 underline leading-snug text-left cursor-pointer font-semibold',
      },
    },
    content: {
      content: `

<span id="why-is-the-flu-jab-recommended-in-pregnancy"></span>

### Why is the flu jab recommended in pregnancy?

The flu jab is recommended in pregnancy because natural changes to the immune system, heart and lungs during pregnancy can make flu more likely to lead to complications such as a chest infection.

Flu is different from a cold. It tends to come on suddenly, with a high temperature, aching muscles and exhaustion, and it can leave you feeling unwell for a week or more.

<div align="center">\n\n
![Digestive changes during menopause](flu-vaccination-blog-first-image.webp "600x400")\n\n
</div>

There is a bonus for your baby, too. Some of the protection you build passes to your baby before birth, helping to protect them in their first few months, when they are too young to have a flu vaccine themselves.

The current recommendations on flu vaccination for pregnant women are published in the NHS guidance on the flu jab in pregnancy. A GMC-registered GP can explain what they mean for you, especially if you have a health condition alongside your pregnancy.

<span id="is-the-flu-jab-safe-at-any-stage-of-pregnancy"></span>

### Is the flu jab safe at any stage of pregnancy?

The flu jab is considered safe at any stage of pregnancy, from the first weeks through to your due date. The vaccine used in pregnancy is an injected, inactivated vaccine, which means it does not contain live virus and cannot give you flu. It is given as a single injection into the upper arm.

<div align="center">\n\n
![How menopause changes the gut microbiome](flu-vaccination-blog-second-image.webp "600x400")\n\n
</div>

If you have questions about the research behind this advice, your midwife or GP can talk you through it, so you can make an informed decision.

Timing is flexible. Most people have the flu jab and pregnancy check-ups running alongside each other in the autumn, but if you become pregnant later in the flu season, you can still have it then. The side effects are the same as for anyone else: usually a sore arm and sometimes mild aches or tiredness for a day or two.

>Have questions about the flu jab and pregnancy? Book a [GP appointment](/private-gp) at LIPS Healthcare, Battersea Power Station.

<span id="can-you-have-the-flu-jab-with-other-pregnancy-vaccines"></span>

### Can you have the flu jab with other pregnancy vaccines?

You can usually have the flu jab at the same appointment as other vaccines offered in pregnancy, such as the whooping cough vaccine, or on a different day.

<div align="center">\n\n
![The estrobolome and oestrogen metabolism](flu-vaccination-blog-third-image.webp "600x400")\n\n
</div>

Other vaccines in pregnancy, including whooping cough and RSV, are recommended at particular stages, while the flu jab can be given at any stage. Your midwife or GP can help you plan the timing so you do not miss any of them. There is no need to leave a gap between the flu jab and other inactivated vaccines unless a clinician advises otherwise, in line with the UKHSA Green Book. If you live in Clapham, Wandsworth or Nine Elms and are balancing several antenatal appointments, it can help to ask about all of these vaccines in one conversation.

<span id="what-happens-at-a-flu-jab-appointment-when-pregnant"></span>

### What happens at a flu jab appointment when pregnant?

A flu jab appointment in pregnancy is short: a clinician checks your health, gives one injection into the upper arm, and may ask you to wait for a few minutes afterwards.

1. You are asked about your pregnancy, health, medicines and any allergies or previous reactions.
2. You have the chance to ask questions before deciding to go ahead.
3. The vaccine is given as a single injection into the upper arm.
4. You may be asked to wait briefly before leaving.
5. Let your midwife know you have had the jab so your records are up to date.

<div align="center">\n\n
![Foods that support gut health during menopause](flu-vaccination-blog-fourth-image.webp "600x400")\n\n
</div>

Afterwards, the usual mild reactions such as a sore arm or tiredness can appear. If you develop a high temperature at any point in pregnancy, contact your midwife, GP or maternity unit for advice, whatever the cause.

GP appointments at LIPS Healthcare take place on the first floor of Turbine Hall B, Battersea Power Station, a short trip on the Northern line or London Overground from Chelsea, Pimlico and Vauxhall. If you feel unwell and need to be seen the same day for something that is not an emergency, the [LIPS Urgent Care Centre](/specialities/urgent-care-centre) can help. For any concern about your pregnancy itself, contact your midwife or maternity unit.

><span style="font-weight: bold; color: #2A347A;">Emergency warning</span>
>
>If you or someone else has signs of a severe allergic reaction, such as swelling of the face, lips or throat, difficulty breathing, a racing heart or collapse, call 999 or go to A&E immediately. LIPS Healthcare is not an emergency service.

><span style="font-weight: bold; color: #2A347A;">Medical disclaimer</span>
>
>This article is for general information only and does not replace advice from a qualified healthcare professional. Guidance on flu vaccination is updated each season. If you have questions about the flu jab and your own health, speak to a doctor, pharmacist or midwife.

<span style="font-weight: bold; color: #000;">Sources</span>

- [NHS: Flu jab in pregnancy](https://www.nhs.uk/pregnancy/keeping-well/flu-jab/)
- [UKHSA: Influenza, the Green Book, chapter 19 (pregnancy, co-administration)](https://www.gov.uk/government/publications/influenza-the-green-book-chapter-19)
- [NHS: Flu vaccine (free eligibility groups)](https://www.nhs.uk/vaccinations/flu-vaccine/)

<span id="frequently-asked-questions"></span>
`,
      styles: {
        h3: 'text-lg md:text-[32px] font-bold text-theme-primary mb-4',
        h4: 'text-lg md:text-[20px] font-bold text-theme-primary mb-4',
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
        a: 'font-semibold text-text-action break-words',
        em: 'not-italic font-bold',
        ul: 'list-disc pl-5 mb-4 space-y-2',
        li: 'text-sm md:text-base text-text-default font-medium leading-relaxed',
        strong:
          'inline-block bg-theme-primary hover:bg-white border border-theme-primary text-white px-6 py-3 rounded-lg font-bold no-underline cursor-pointer my-4 text-center [&_a]:text-white [&_a]:no-underline [&_a]:font-bold hover:[&_a]:text-theme-primary',
        table:
          'w-full table-fixed border-collapse text-xs md:text-sm border-2 border-black',
        th: 'border-2 border-black bg-text-muted-blue px-2 py-2 md:px-4 md:py-3 text-left font-semibold text-theme-primary whitespace-normal wrap-break-word',
        td: 'border-2 border-black px-2 py-2 md:px-4 md:py-3 align-top font-semibold whitespace-normal wrap-break-word',
        blockquote:
          'border-l-4 border-theme-primary bg-[#E0E7FF] rounded-lg pt-4 px-4 pb-2 my-8 text-sm text-text-default not-italic',
      },
      tableOptions: {
        width: '100%',
        columnWidths: ['50%', '50%'],
      },
    },
    faq: {
      heading: {
        content:
          '#### Frequently asked questions about flu vaccination in pregnancy',
        styles: {
          h4: 'text-xl md:text-2xl font-bold text-theme-primary mb-6',
        },
      },
      items: [
        {
          question: {
            content: 'When is the best time to have the flu jab in pregnancy?',
          },
          answer: {
            content:
              'As soon as it is offered. You can have it at any stage, so there is no benefit in waiting for a particular trimester, and having it in autumn means you are protected before flu usually circulates most widely.',
          },
        },
        {
          question: {
            content: 'Can I have the flu jab while breastfeeding?',
          },
          answer: {
            content:
              'Yes. The flu jab is considered safe while breastfeeding, and you do not need to stop feeding to have it.',
          },
        },
        {
          question: {
            content: 'Will the flu jab protect my baby?',
          },
          answer: {
            content:
              'Yes, for the first few months. Some of the protection you build in pregnancy passes to your baby, which helps protect them before they are old enough for their own vaccine.',
          },
        },
        {
          question: {
            content: 'Can I have the flu jab in my first trimester?',
          },
          answer: {
            content:
              'Yes. The flu jab can be given at any stage of pregnancy, including the first 12 weeks.',
          },
        },
        {
          question: {
            content: 'Is flu vaccination for pregnant women free?',
          },
          answer: {
            content:
              'Yes, usually. Pregnant women are among the groups offered a free flu vaccine each season. Check current eligibility on NHS.uk. If you choose a private appointment, our guide to how much a flu jab costs explains what affects the price.',
          },
        },
        {
          question: {
            content:
              'I had the flu jab before I knew I was pregnant. Is that a problem?',
          },
          answer: {
            content:
              'No, it is not considered a problem, because the flu jab is safe at any stage of pregnancy. Let your midwife know so it is recorded.',
          },
        },
      ],
    },
  },
];
